import React, { Component } from 'react';
import styled from 'styled-components';
import { CloseIcon } from '@ui/svgs/CloseIcon';
import {
  ALGOLIA_HITS_PER_PAGE,
  ALGOLIA_MAX_FACETS_HITS,
  FACET_CATEGORIES,
  GRID,
} from '../../../../lib/constants';
import LayoutCollectionView from './LayoutCollectionView';
import FilterIcon from '../../../svgs/FilterIcon';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import MaxWidthLayout from '../../../templates/MaxWidthLayout';
import FilterModal from '../FilterModal/FilterModal';
import CategoryFilterComponent from '../FilterModal/CategoryFilterComponent';
import { apiSearchAlgolia, apiSearchForFacetsAlgolia } from '../../../../lib/apiQueries';
import { replaceBrowserUrl } from '../../../../lib/utils';

const FilterLayoutDiv = styled.div`
  display: flex;
  flex-direction: row;
  min-height: ${(p) => p.theme.unit(85)};
`;

const FilterContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: ${(p) => p.theme.unit(50)};
  margin-top: ${(p) => (p.hasTitle ? p.theme.unit(4) : p.theme.unit(1.5))}
`;

const SubcategoryTextDiv = styled.div`
  ${({ theme }) => theme.typographyRebrand.heading['2xl']}
  color: ${(p) => p.theme.c.text.primary};
  margin-bottom: ${(p) => p.theme.unit(1.5)};
`;

const ClearFiltersButton = styled.button`
  ${({ theme }) => theme.typographyRebrand.body.md}
  border: none;
  background-color: transparent;
  color: ${(p) => p.theme.c.button.light.secondary.text.default};
  :hover {
    text-decoration: underline;
  }
  cursor: pointer;

  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.unit(0.5)};

  padding: ${({ theme }) => theme.unit(0.25)} ${({ theme }) => theme.unit(1)};
  margin-bottom: ${({ theme }) => theme.unit(1)};
`;

// Mobile
const MobileFilterLayoutDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ClearFilterIcon = styled(CloseIcon)`
  width: ${({ theme }) => theme.unit(2)};
  height: ${({ theme }) => theme.unit(2)};
  margin-right: ${({ theme }) => theme.unit(0.5)};
  
  color: ${({ theme }) => theme.c.icon.interactive};
`;

const FilterButton = styled.button`
  ${({ theme }) => theme.typographyRebrand.labels.buttonsLg}
  color: ${({ theme }) => theme.c.text.interactive};
  border: solid 2px ${(p) => p.theme.c.button.light.primary.border.default};

  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
  padding: ${(p) => p.theme.unit(2)};
  box-sizing: border-box;
  cursor: pointer;
  background-color: transparent;

  & svg {
    color: ${({ theme }) => theme.c.icon.interactive};
  }
`;

const FilterIconsDiv = styled.div`
  display: flex;
  flex-direction: row;

  gap: ${(p) => p.theme.unit(1)};
`;

const FilterActiveCountDiv = styled.div`
  padding: 0 ${({ theme }) => theme.unit(1)};
  background-color: ${({ theme }) => theme.c.bg['feature-1']};

  border-radius: ${(p) => p.theme.unit(0.5)};
`;

const FilterSectionTitleDiv = styled.div`
  ${({ theme }) => theme.typographyRebrand.heading.xl}
  color: ${p => p.theme.c.text.primary};
  padding: ${p => p.theme.unit(4)} 0;
`;

class LayoutFilterableCollectionView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      shopHitsData: [],
      categoryFiltersDictionary: {},
    };

    this.toggleFilter = this.toggleFilter.bind(this);
    this.searchShops = this.searchShops.bind(this);
    this.clearFilters = this.clearFilters.bind(this);
    this.applyFilters = this.applyFilters.bind(this);
    this.getSubCategorySlugsFromUrl = this.getSubCategorySlugsFromUrl.bind(this);
    this.updateCategoryDictionary = this.updateCategoryDictionary.bind(this);
    this.replaceFilterQueryParams = this.replaceFilterQueryParams.bind(this);
  }

  componentDidMount() {
    const {
      parentCategoryFilter = '',
      parentSubCategoryNames = [],
      locale,
    } = this.props;

    const categoryFacetSearchOptions = {
      facetName: FACET_CATEGORIES,
      facetQuery: '',
      maxFacetHits: ALGOLIA_MAX_FACETS_HITS,
    };

    if (parentCategoryFilter) {
      categoryFacetSearchOptions['filters'] = `${FACET_CATEGORIES}:'${parentCategoryFilter}'`;
    }

    apiSearchForFacetsAlgolia(categoryFacetSearchOptions, 'shops', locale)
      .then((content) => {
        // remove parent category from the facet list, and only keep the subcategories
        const categoryFacets = content.filter((f) => {
          return (f.value.trim() !== parentCategoryFilter && parentSubCategoryNames.includes(f.value));
        });

        const categoryKeyDictionary = categoryFacets.reduce((acc, facet) => {
          acc[facet.value] = false;
          return acc;
        }, {});

        const dictionary = this.updateCategoryDictionary(categoryKeyDictionary);
        this.applyFilters(dictionary);
      })
      .catch((err) => console.error(err));
  }

  getSubCategorySlugsFromUrl() {
    if (!window) {
      return [];
    }

    const params = new URLSearchParams(window.location.search);
    return params.getAll('subCategory').filter(Boolean);
  }

  updateCategoryDictionary(dictionary) {
    const {
      parentSubCategories = [],
    } = this.props;
    const subCategorySlugs = this.getSubCategorySlugsFromUrl();
    const dictionaryClone = { ...dictionary };
    const subCategorySlugNameMap = parentSubCategories.reduce((acc, subCategory) => {
      if (subCategory?.slug && subCategory?.name) {
        acc[subCategory.slug] = subCategory.name;
      }
      return acc;
    }, {});

    if (subCategorySlugs.length) {
      subCategorySlugs.forEach((slug) => {
        const category = decodeURIComponent(subCategorySlugNameMap[slug] || '');
        if (dictionaryClone.hasOwnProperty(category)) {
          dictionaryClone[category] = true;
        }
      });
    }

    return dictionaryClone;
  }

  searchShops() {
    const {
      searchQuery = '',
      parentCategoryFilter,
      locale,
    } = this.props;

    const activeFilters = Object.keys(this.state.categoryFiltersDictionary)
      .reduce((acc, catFilter) => {
        if (this.state.categoryFiltersDictionary[catFilter]) {
          acc.push(catFilter);
        }
        return acc;
      }, []);
    let facetFilters = activeFilters.map((f) => `${FACET_CATEGORIES}:${f}`);
    facetFilters = [facetFilters]; // query for a union of filters using nested arrays (Algolia format)

    const searchQueryOptions = {
      facets: [FACET_CATEGORIES],
      facetFilters,
      hitsPerPage: ALGOLIA_HITS_PER_PAGE,
    };

    if (parentCategoryFilter) {
      searchQueryOptions['filters'] = `${FACET_CATEGORIES}:'${parentCategoryFilter}'`;
    }

    apiSearchAlgolia(searchQuery, searchQueryOptions, 'shops', locale)
      .then((content) => {
        this.setState({ shopHitsData: content.hits });
      })
      .catch((err) => console.error(err));
  }

  toggleFilter(filter) {
    this.state.categoryFiltersDictionary[filter] = !this.state.categoryFiltersDictionary[filter];
    this.replaceFilterQueryParams(this.state.categoryFiltersDictionary);
    this.searchShops();
  }

  replaceFilterQueryParams(categoriesFilter) {
    if (typeof window === 'undefined') {
      return;
    }

    const {
      parentSubCategories = [],
    } = this.props;
    const subCategoryNameSlugMap = parentSubCategories.reduce((acc, subCategory) => {
      if (subCategory?.name && subCategory?.slug) {
        acc[subCategory.name] = subCategory.slug;
      }
      return acc;
    }, {});

    const subCategorySlugs = Object.keys(categoriesFilter)
      .filter((categoryName) => categoriesFilter[categoryName])
      .map((categoryName) => subCategoryNameSlugMap[categoryName])
      .filter(Boolean);

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('subCategory');
    subCategorySlugs.forEach((slug) => currentUrl.searchParams.append('subCategory', slug));
    replaceBrowserUrl(currentUrl.toString());
  }

  clearFilters() {
    Object.keys(this.state.categoryFiltersDictionary).forEach((filterKey) => {
      this.state.categoryFiltersDictionary[filterKey] = false;
    });
    this.replaceFilterQueryParams(this.state.categoryFiltersDictionary);
    this.searchShops();
  }

  applyFilters(newCategoryFilters) {
    this.setState({
      categoryFiltersDictionary: newCategoryFilters,
    }, this.searchShops);
  }

  render() {
    const {
      favItemIds,
      parentCategoryFilter,
      locale,
      pageStateModifiers,
      clearFiltersText,
      cancelFilterText,
      applyFilterButtonText,
      filterSectionTitleText,
      layoutTitleText,
      buttonText,
      buttonUrl,
      tags,
      itemType,
      Comp,
      saveItemHandler,
      removeItemHandler,
      pageContext,
    } = this.props;

    const categoryFilters = this.state.categoryFiltersDictionary;
    const subcategoryFilterTitleText = (Object.keys(categoryFilters).length !== 0) ? this.props.subcategoryFilterTitleText : '';

    const numActiveFilters = Object.keys(categoryFilters)
      .reduce((acc, facet) => {
        if (categoryFilters[facet]) {
          return acc + 1;
        }
        return acc;
      }, 0);

    const makeAndOpenModal = () => {
      const body = (
        <FilterSectionTitleDiv>
          {filterSectionTitleText}
        </FilterSectionTitleDiv>
      );
      if (this.filterModal) {
        this.filterModal.updateModalBody(body, this.state.categoryFiltersDictionary);
      }
    };

    const itemsResult = (
      <LayoutCollectionView
        favItemIds={favItemIds}
        items={this.state.shopHitsData}
        Comp={Comp}
        itemType={itemType}
        viewType={GRID}
        saveItemHandler={saveItemHandler}
        removeItemHandler={removeItemHandler}
        pageStateModifiers={pageStateModifiers}
        locale={locale}
        sectionTitle={layoutTitleText}
        showDisplayName
        buttonText={buttonText}
        buttonUrl={buttonUrl}
        tags={tags}
        pageContext={pageContext}
      />
    );

    const desktopBody = (
      <FilterLayoutDiv>
        <FilterContainerDiv hasTitle={!!layoutTitleText}>
          <SubcategoryTextDiv>
            {subcategoryFilterTitleText}
          </SubcategoryTextDiv>
          {numActiveFilters > 0 && (
            <ClearFiltersButton onClick={this.clearFilters}>
              <ClearFilterIcon />
              {clearFiltersText}
            </ClearFiltersButton>
          )}
          <CategoryFilterComponent
            categoryFiltersDictionary={categoryFilters}
            toggleFilter={this.toggleFilter} />
        </FilterContainerDiv>
        {itemsResult}
      </FilterLayoutDiv>
    );

    const mobileBody = (
      <MobileFilterLayoutDiv>
        <FilterButton onClick={makeAndOpenModal}>
          <span>
            { filterSectionTitleText }
          </span>
          <FilterIconsDiv>
            {numActiveFilters > 0
            && (
            <FilterActiveCountDiv>
              {numActiveFilters}
            </FilterActiveCountDiv>
            )}
            <FilterIcon />
          </FilterIconsDiv>
        </FilterButton>
        {itemsResult}
      </MobileFilterLayoutDiv>
    );

    const renderComp = (
      <div>
        <MaxWidthLayout>
          <MediaQueryHelper
            desktopChildren={desktopBody}
            mobileChildren={mobileBody} />
        </MaxWidthLayout>
        <FilterModal
          ref={(element) => { this.filterModal = element; }}
          subcategoryFilterTitleText={subcategoryFilterTitleText}
          applyFilterButtonText={applyFilterButtonText}
          clearFiltersText={clearFiltersText}
          cancelFilterText={cancelFilterText}
          filterSectionTitleText={filterSectionTitleText}
          replaceFilterHash={this.replaceFilterQueryParams}
          applyFiltersCb={this.applyFilters} />
      </div>
    );
    return renderComp;
  }
}

export default LayoutFilterableCollectionView;
